import React, { ReactNode } from 'react';

import {
  Close,
  Copy,
  SnsFacebook,
  SnsLine,
  SnsTwitter,
  SnsViber,
  SnsWhatsApp,
} from '@travel/icons/ui';
import useDeviceType, { MOBILE_SCREEN } from '@travel/traveler-core/hooks/useDeviceType';
import cx from '@travel/ui/utils/classnames';

import { Translate } from 'core/translate';

import styles from './shareMenu.module.scss';

type ShareItemProps = {
  name: string;
  icon: JSX.Element;
  href: string;
  translationId: string;
  onClick?: () => void;
};

type Props = {
  /** Custom style for wrapper */
  wrapperClassName?: string;
  /** Custom style for menu wrapper */
  className?: string;

  /** Custom widget header */
  customHeader?: ReactNode;

  /** Localized promotional text for sharing URL. Defaults to 'url' prop if not present */
  sharePromoTranslation?: string;

  /** Plain URL for url-only sharing links (FB,Line,etc.) */
  url: string;

  /** function to call on dialog close */
  onClose?: () => void;
};

const ShareMenuItem = (props: ShareItemProps) => {
  const { name, icon, href, translationId, onClick } = props;
  return (
    <div className={styles.shareItem}>
      <a
        target="_blank"
        rel="noopener noreferrer"
        href={href}
        className={styles.shareItemLink}
        data-testid={`shareMenu-link-${name}`}
        onClick={onClick}
      >
        {icon}
        <div className={styles.menuItemName}>
          <Translate id={translationId} />
        </div>
      </a>
    </div>
  );
};

export default function ShareMenu(props: Props) {
  const {
    className,
    onClose,
    wrapperClassName,
    sharePromoTranslation,
    customHeader,
    url,
    ...rest
  } = props;
  const deviceType = useDeviceType();
  const socialShareText = sharePromoTranslation ?? url;
  const isMobile = deviceType === MOBILE_SCREEN;

  const copyCurrentUrl = () => {
    const input = document.createElement('input');

    document.body.appendChild(input);
    input.value = url;
    input.select();
    document.execCommand('copy');
    document.body.removeChild(input);

    onClose?.();
  };

  const snsItems = [
    {
      name: 'facebook',
      icon: <SnsFacebook className={styles.menuItemIcon} size={21} />,
      href: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      translationId: 'Traveler_Common.Share.Facebook',
    },
    {
      name: 'whatsapp',
      icon: <SnsWhatsApp className={styles.menuItemIcon} />,
      href: `https://api.whatsapp.com/send?text=${socialShareText}`,
      translationId: 'Traveler_Common.Share.Whatsapp',
    },
    {
      name: 'twitter',
      icon: <SnsTwitter className={styles.menuItemIcon} />,
      href: `https://twitter.com/intent/tweet?text=${socialShareText}`,
      translationId: 'Traveler_Common.Share.Twitter',
    },
    {
      name: 'line',
      icon: <SnsLine className={styles.menuItemIcon} />,
      href: `https://social-plugins.line.me/lineit/share?url=${url}`,
      translationId: 'Traveler_Common.Share.Line',
    },
    {
      name: 'viber',
      icon: <SnsViber className={styles.menuItemIcon} />,
      href: `viber://forward?text=${socialShareText}`,
      translationId: 'Traveler_Common.Share.Viber',
    },
  ] as Array<ShareItemProps>;

  return (
    <div {...rest} className={wrapperClassName} data-testid="shareMenu-wrapper">
      <div className={cx(className, styles.shareMenu)} data-testid="shareMenu-container">
        {isMobile && !customHeader && (
          <div className={styles.shareMenuHeader}>
            <span className={styles.shareMenuTitle}>
              <Translate id="Traveler_Common.Provider_List.Share" />
            </span>
            <button className={styles.button} onClick={onClose} data-testid="shareMenu-closeButton">
              <Close size={20} />
            </button>
          </div>
        )}
        {customHeader}
        <div className={styles.shareItemContainer}>
          {snsItems.map(item => (
            <ShareMenuItem
              key={item.name}
              name={item.name}
              icon={item.icon}
              href={item.href}
              translationId={item.translationId}
              onClick={onClose}
            />
          ))}
          <div className={styles.shareItem}>
            <button
              onClick={copyCurrentUrl}
              className={cx(styles.button, styles.shareItemLink)}
              data-testid="shareMenu-copyButton"
            >
              <Copy className={styles.menuItemIcon} />
              <div className={styles.menuItemName}>
                <Translate id="Traveler_Common.Share.Copy" />
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

ShareMenu.defaultProps = {
  onClose: () => {},
};
